import { useMemo, useState } from 'react';
import { LayoutGrid, PackageSearch, Plus, Rows3, Search } from 'lucide-react';
import type { StockPlaceDto, Unit } from '@eombor/contracts';
import { Button, Card, EmptyState, Input, Skeleton, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { BackButton } from '../../../shared/ui/BackButton.js';
import { Select } from '../../../shared/ui/Select.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { useNomenclatures } from '../../catalog/model/catalog.queries.js';
import { useWarehouses } from '../../warehouse/model/warehouse.queries.js';
import { useStockSearch } from '../model/stock.queries.js';
import { BatchHistoryDialog } from './BatchHistoryDialog.js';
import { StockProductCard } from './StockProductCard.js';
import { StockLocationDialog } from './StockLocationDialog.js';
import { BatchLabelDialog, type BatchLabelTarget } from './BatchLabelDialog.js';
import { StockResultRow } from './StockResultRow.js';

interface HistoryTarget {
  batchId: string;
  batchNo: string;
  unit: Unit | null;
}

export interface StockSearchPageProps {
  /** Kirim ekraniga o'tish — sahifa marshrutlashni bilmaydi. */
  onNewReceipt?: () => void;
  /**
   * Talabnoma yaratish ekraniga o'tish.
   *
   * Savat SHU sahifada emas: buyurtma qilish uchun ombor, izoh va
   * miqdorlar kerak va ular talabnoma ekranida turadi. Shuning uchun bu
   * yerda BITTA havola bo'ladi, har kartochkada o'chirilgan tugma emas.
   */
  onNewRequisition?: () => void;
  /** Omborlar ro'yxatiga qaytish. Berilmasa tugma chizilmaydi. */
  onBack?: () => void;
  /**
   * Berilsa — sahifa AYNAN shu ombor ichida turadi (ombor kartochkasining
   * "Mahsulotlar" ko'rinishi): qidiruv shu ombor bilan chegaralanadi, ombor
   * filtri va sahifa sarlavhasi chizilmaydi — kartochka ularni allaqachon
   * bergan. Berilmasa — butun doira bo'ylab qidiruv, o'z sarlavhasi bilan.
   */
  warehouseId?: string;
}

/**
 * Mahsulotlar qidiruvi — buyurtmachining birinchi ekrani.
 *
 * Qidiruv MTU doirasida ishlaydi: doirani backend qo'yadi (`stock.read`
 * granti), sahifa esa faqat ombor filtrini beradi. "Faqat mavjud" filtri
 * so'rovga `onlyAvailable=true` bo'lib ketadi va band qilingan mahsulotni
 * chiqarib tashlaydi — "bor" deb ko'rilgan narsa boshqa talabnomaga band bo'lib
 * turmasligi kerak.
 */
export const StockSearchPage = ({
  onNewReceipt,
  onNewRequisition,
  onBack,
  warehouseId: fixedWarehouseId,
}: StockSearchPageProps) => {
  const { can, canAnywhere } = usePermission();

  /* Ombor kartochkasi ichida turganda qidiruv doirasi TASHQARIDAN keladi va
     ichkaridagi filtr umuman chizilmaydi — bir ekranda ikki xil "qaysi
     ombor?" javobini bermaslik uchun. */
  const embedded = fixedWarehouseId !== undefined;

  const [q, setQ] = useState('');
  const [pickedWarehouseId, setPickedWarehouseId] = useState('');
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [history, setHistory] = useState<HistoryTarget | null>(null);
  /* «Qayerda turibdi» oynasi — katak kodi bosilganda. Ombor ham
     saqlanadi: mahsulot bir necha omborda yotishi mumkin va oyna
     FAQAT bittasining chizmasini ko'rsatadi. */
  const [locating, setLocating] = useState<{
    warehouseId: string;
    nomenclatureId: string;
    name: string;
  } | null>(null);

  /* Etiketka bosiladigan partiyalar — qidiruv qatoridan. Ro'yxat
     bo'lib turadi, chunki oynaning o'zi bir nechta partiyani ham
     qabul qiladi (kirim hujjatida shunday ishlatiladi). */
  const [labelling, setLabelling] = useState<BatchLabelTarget[] | null>(null);

  /*
   * IKKI YUZ, bitta sahifa.
   *
   * Savol kim qarayotganiga qarab boshqacha. Mahsulotni QIMIRLATADIGAN
   * odam "qaysi katakdagi qaysi partiyada?" deb so'raydi — unga jadval
   * kerak. Buyurtmachi esa "bormi, qancha va qaysi ombordan?" deb
   * so'raydi — unga partiya raqami va tarix ekranni to'ldirib, asosiy
   * sonni ko'rinmas qilib qo'yadi.
   *
   * Shuning uchun ko'rinish RUXSATDAN kelib chiqadi, tanlovdan emas;
   * ikkalasi ham bo'lgan odam (masalan superadmin) almashtira oladi.
   */
  const movesStock =
    canAnywhere('stock.receive') || canAnywhere('stock.issue') || canAnywhere('warehouse.layout.edit');
  const [view, setView] = useState<'places' | 'cards'>(movesStock ? 'places' : 'cards');

  const warehouseId = fixedWarehouseId ?? pickedWarehouseId;

  const stock = useStockSearch({
    ...(q === '' ? {} : { q }),
    ...(warehouseId === '' ? {} : { warehouseId }),
    onlyAvailable,
    limit: 20,
  });

  /* Ombor ro'yxati faqat FILTR uchun kerak — kartochka ichida u chizilmaydi,
     demak so'rov ham ketmaydi. */
  const warehouses = useWarehouses({ limit: 200 }, { enabled: !embedded });

  /* Kam qoldiq chegarasi KATALOGDAN olinadi: qidiruv javobida `minStock` yo'q
     (u nomenklaturaning xossasi). Ro'yxat uzun `staleTime` bilan keladi. */
  const nomenclatures = useNomenclatures({ limit: 200 });
  const thresholds = useMemo(
    () => new Map((nomenclatures.data?.items ?? []).map((item) => [item.id, item.minStock])),
    [nomenclatures.data],
  );
  const units = useMemo(
    () => new Map((nomenclatures.data?.items ?? []).map((item) => [item.id, item.unit])),
    [nomenclatures.data],
  );

  const items = stock.data?.items ?? [];
  /* Ombor ma'lum bo'lsa ruxsat AYNAN shu ombor uchun so'raladi: boshqa MTU ga
     berilgan `stock.receive` bu omborda tugma chizmasligi kerak. */
  const canReceive = embedded
    ? can('stock.receive', { warehouseId: fixedWarehouseId })
    : canAnywhere('stock.receive');
  const canOrder = canAnywhere('requisition.create');

  const openHistory = (place: StockPlaceDto, nomenclatureId: string): void => {
    setHistory({
      batchId: place.batchId,
      batchNo: place.batchNo,
      unit: units.get(nomenclatureId) ?? null,
    });
  };

  /* Boshqaruvlar IKKALA holatda ham bir xil. Ichma-ich chizilganda (ombor
     kartasi ichida) sarlavha yo'q — sahifaning nomi tepada allaqachon
     turadi va ikkinchisi faqat takror bo'lardi. */
  const controls = (
    <>
      <Input
        variant="filter"
        label={t('stock.searchLabel')}
        placeholder={t('stock.searchPlaceholder')}
        prefix={<Search size={16} aria-hidden />}
        value={q}
        onChange={(event) => setQ(event.target.value)}
      />

      {embedded ? null : (
        <Select
          variant="filter"
          label={t('stock.filterWarehouse')}
          placeholder={t('stock.filterAllWarehouses')}
          value={pickedWarehouseId}
          onChange={(event) => setPickedWarehouseId(event.target.value)}
          options={(warehouses.data?.items ?? []).map((warehouse) => ({
            value: warehouse.id,
            label: warehouse.name,
          }))}
        />
      )}

      <label
        className={cn(
          'flex h-[var(--size-control-md)] items-center gap-2 rounded-[var(--radius-sm)]',
          'border border-border-control px-3 text-sm text-text',
        )}
      >
        <input
          type="checkbox"
          className="size-4 accent-[var(--color-accent)]"
          checked={onlyAvailable}
          onChange={(event) => setOnlyAvailable(event.target.checked)}
        />
        {t('stock.onlyAvailable')}
      </label>

      {/* Ko'rinish almashgichi FAQAT ikkala ish ham tegishli odamda:
          buyurtmachiga jadval ko'rinishi hech qachon kerak emas va
          bo'sh tanlov faqat savol tug'dirardi. */}
      {movesStock ? (
        <div
          role="group"
          aria-label={t('stock.viewLabel')}
          /* Ichki bo'shliq 4px → 2px: 36px tugma 40px konteynerga sig'sin.
             Ilgari konteyner 44px edi va yonidagi tanlagichdan baland
             turardi. */
          className="border-border-control flex h-[var(--size-control-md)] items-center gap-1 rounded-[var(--radius-sm)] border p-0.5"
        >
          <Button
            size="sm"
            variant={view === 'places' ? 'secondary' : 'ghost'}
            aria-pressed={view === 'places'}
            onClick={() => setView('places')}
          >
            <Rows3 size={14} aria-hidden />
            {t('stock.viewPlaces')}
          </Button>
          <Button
            size="sm"
            variant={view === 'cards' ? 'secondary' : 'ghost'}
            aria-pressed={view === 'cards'}
            onClick={() => setView('cards')}
          >
            <LayoutGrid size={14} aria-hidden />
            {t('stock.viewCards')}
          </Button>
        </div>
      ) : null}
    </>
  );

  const actions = (
    <>
      {canReceive && onNewReceipt !== undefined ? (
        <Button onClick={onNewReceipt}>
          <Plus size={16} aria-hidden />
          {t('stock.newReceipt')}
        </Button>
      ) : null}

      {canOrder && onNewRequisition !== undefined ? (
        <Button variant={movesStock ? 'secondary' : 'primary'} onClick={onNewRequisition}>
          <Plus size={16} aria-hidden />
          {t('stock.newRequisition')}
        </Button>
      ) : null}
    </>
  );

  return (
    <div className="flex flex-col gap-5">
      {embedded ? (
        <div className="flex flex-wrap items-end gap-3">
          {controls}
          {actions}
        </div>
      ) : (
        <PageHeader
          title={t('stock.title')}
          description={t('stock.subtitle')}
          filtersLabel={t('stock.title')}
          back={
            onBack === undefined ? undefined : (
              <BackButton onClick={onBack} className="mb-1 self-start">
                {t('warehouse.backToList')}
              </BackButton>
            )
          }
          filters={controls}
          actions={actions}
        />
      )}

      {stock.isPending ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : stock.isError ? (
        <Card>
          <EmptyState
            title={t('stock.searchError')}
            description={stock.error instanceof ApiClientError ? stock.error.message : undefined}
            action={
              <Button variant="secondary" onClick={() => void stock.refetch()}>
                {t('stock.retry')}
              </Button>
            }
          />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<PackageSearch size={20} aria-hidden />}
            title={t('stock.searchEmpty')}
            description={t('stock.searchEmptyHint')}
          />
        </Card>
      ) : (
        <>
          {view === 'cards' ? (
            <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((group) => (
                <StockProductCard
                  key={group.nomenclatureId}
                  group={group}
                  minStock={thresholds.get(group.nomenclatureId) ?? null}
                />
              ))}
            </ul>
          ) : (
            <ul className="flex flex-col gap-4">
              {items.map((group) => (
                <StockResultRow
                  key={group.nomenclatureId}
                  group={group}
                  minStock={thresholds.get(group.nomenclatureId) ?? null}
                  onShowHistory={(place) => openHistory(place, group.nomenclatureId)}
                  onShowLocation={(place) =>
                    setLocating({
                      warehouseId: place.warehouseId,
                      nomenclatureId: place.nomenclatureId,
                      name: place.nomenclatureName,
                    })
                  }
                  /* Etiketka ruxsati OMBOR bo'yicha: satr o'z omborini
                     biladi va begona omborda tugma umuman chizilmaydi. */
                  /* Ruxsat KENG tekshiriladi (`canAnywhere`): qator bir
                     nechta omborning satrini ko'rsatadi va bu yerda
                     qaysi biri bosilishini oldindan bilib bo'lmaydi.
                     Doirani server har partiya uchun o'zi tekshiradi. */
                  {...(canAnywhere('warehouse.label.print')
                    ? {
                        onPrintLabel: (place: StockPlaceDto) =>
                          setLabelling([
                            {
                              batchId: place.batchId,
                              batchNo: place.batchNo,
                              name: place.nomenclatureName,
                              hint: `${place.cellCode} · ${place.warehouseName}`,
                            },
                          ]),
                      }
                    : {})}
                />
              ))}
            </ul>
          )}
          <p className="tabular text-xs text-text-faint">
            {t('stock.searchTotal', { count: stock.data?.total ?? items.length })}
          </p>
        </>
      )}

      <BatchLabelDialog
        open={labelling !== null}
        onClose={() => setLabelling(null)}
        targets={labelling ?? []}
      />

      <StockLocationDialog
        open={locating !== null}
        onClose={() => setLocating(null)}
        warehouseId={locating?.warehouseId ?? ''}
        nomenclatureId={locating?.nomenclatureId ?? ''}
        nomenclatureName={locating?.name ?? ''}
      />

      <BatchHistoryDialog
        open={history !== null}
        onClose={() => setHistory(null)}
        batchId={history?.batchId ?? null}
        batchNo={history?.batchNo ?? ''}
        unit={history?.unit ?? null}
      />
    </div>
  );
};
